import { Helmet } from "react-helmet-async";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Smartphone } from "lucide-react";
import { supportedLanguages } from "../supportedLanguages";

const ReleaseNotes: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  const location = useLocation();
  const langMatch = location.pathname.match(/^\/([a-z]{2})\//);
  const urlLang = langMatch ? langMatch[1] : null;
  const canonicalUrl = `https://bible4.me/${urlLang || lang || "fr"}/features`;
  const [sections, setSections] = useState<{ title: string; content: string[] }[]>([]);

  useEffect(() => {
    let selectedLang = urlLang || lang || i18n.language || navigator.language.split("-")[0] || "fr";
    if (!supportedLanguages.includes(selectedLang)) {
      selectedLang = "fr";
    }
    if (i18n.language !== selectedLang) {
      console.log("Changing language to:", selectedLang);
      i18n.changeLanguage(selectedLang);
    }
  }, [urlLang, lang, i18n]);

  useEffect(() => {
    const fetchReleaseNotes = async () => {
      let selectedLang = urlLang || lang || i18n.language || navigator.language.split("-")[0] || "fr";
      if (!supportedLanguages.includes(selectedLang)) {
        selectedLang = "fr";
      }
      let response = await fetch(`/locales/${selectedLang}/google_play_${selectedLang}.md`);
      if (!response.ok && selectedLang !== "fr") {
        response = await fetch(`/locales/fr/google_play_fr.md`);
      }
      if (!response.ok && selectedLang !== "en") {
        response = await fetch(`/locales/en/google_play_en.md`);
      }
      try {
        if (response.ok) {
          const text = await response.text();
          const parsedSections = parseReleaseNotes(text);
          setSections(parsedSections);
        } else {
          setSections([{ title: t("errors.contentNotFound", "Error"), content: [t("errors.fetchFailed", "Release notes not available.")] }]);
        }
      } catch (error) {
        setSections([{ title: t("errors.contentNotFound", "Error"), content: [t("errors.loadingFailed", "Error loading release notes.")] }]);
      }
    };

    fetchReleaseNotes();
  }, [urlLang, lang, i18n.language, t]);

  const parseReleaseNotes = (text: string): { title: string; content: string[] }[] => {
    const regex = /<([a-zA-Z-]+)>([\s\S]*?)<\/\1>/g;
    const sections: { title: string; content: string[] }[] = [];
    let match;

    while ((match = regex.exec(text)) !== null) {
      const lines = match[2]
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      if (lines.length > 0) {
        // Convert the first line format from 12(1.0.0) to 1.0.0+12
        const title = lines[0].replace(/(\d+)\(([\d.]+)\)/, "$2+$1");
        const content = lines.slice(1).map((line) => (line.startsWith("+") ? line.replace("+", "").trim() : line));
        sections.push({ title, content });
      }
    }
    return sections;
  };
  const handleJoinBeta = () => {
    navigate("/download");
  };

  return (
    <>
      <Helmet>
        <title>{t('seo.features.title')}</title>
        <meta name="description" content={t('seo.features.description')} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <div className="bg-amber-50 min-h-screen flex flex-col">
      {/* Banner Header */}
      <div className="bg-white shadow-lg z-50 w-full">
        <div className="flex items-center p-3 max-w-6xl mx-auto">
          <div className="w-12 h-12 flex items-center justify-center mr-3">
            <img 
              src="/logo%20626x626.png" 
              alt={String(t("home.title", "Bible4Me"))}
              className="w-full h-full object-contain rounded-lg"
            />
          </div>
          <h3 className="font-semibold text-amber-900 flex-1">{t("home.title", "Bible4Me")}</h3>
          <div className="flex items-center gap-2">            <a 
              href="/download"
              onClick={handleJoinBeta}
              className="bg-amber-100 text-amber-900 px-4 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-amber-200 transition-colors"
            >
              <Smartphone size={16} />
              <span>{t("home.appBanner.get", "Download Beta")}</span>
            </a>
          </div>
        </div>
      </div>
      {/* Back Button */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        <button
          onClick={() => navigate(`/${urlLang || lang || "fr"}/`)}
          className="flex items-center gap-2 text-amber-900 hover:text-amber-700 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>{t("common.goBack", "Back to Home")}</span>
        </button>
      </div>
      {/* Release Notes Content */}
      <div className="p-4 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md shadow-md max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-4">{t("home.features.title", "Release Notes")}</h1>
        {sections.map((section, index) => (
          <div key={index} className="mb-6">
            <h2 className="text-xl font-semibold mb-2">{section.title}</h2>
            <ul className="list-disc list-inside space-y-1">
              {section.content.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {/* Compact Call to Join Beta */}
      <div className="bg-amber-200 py-4 mt-8">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-lg text-amber-800 mb-4">
            {t("home.cta.description", "Experience the latest features by joining our beta program.")}
          </p>
          <button
            onClick={handleJoinBeta}
            className="bg-amber-900 hover:bg-amber-800 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Smartphone size={20} />
            <span>{t("home.joinBeta", "Join Beta")}</span>
          </button>
        </div>
      </div>
      {/* Footer */}
      <footer className="bg-amber-900 text-white py-8 px-4 mt-auto">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 flex items-center justify-center">
                <img
                  src="/logo%20626x626.png"
                  alt={String(t("home.title", "Bible4Me"))}
                  className="w-full h-full object-contain rounded-lg"
                />
              </div>
              <span className="font-bold">{t("home.title", "Bible4Me")}</span>
            </div>
            <div className="flex flex-wrap justify-center gap-6">
              <a 
                href="/features" 
                onClick={() => navigate('/features')}
                className="hover:text-amber-200 transition-colors"
              >
                {t("home.features.title", "Features")}
              </a>
              <a 
                href="/privacy" 
                onClick={() => navigate('/privacy')}
                className="hover:text-amber-200 transition-colors"
              >
                {t("privacy.title", "Privacy Policy")}
              </a>
              <a 
                href="/download" 
                onClick={handleJoinBeta}
                className="hover:text-amber-200 transition-colors"
              >
                {t("home.downloadApp", "Download App")}
              </a>
            </div>
          </div>
          <div className="mt-8 text-center text-amber-300 text-sm">
            <p>© 2025 {t("home.title", "Bible4Me")}. {t("common.allRightsReserved", "All rights reserved.")}</p>
          </div>
        </div>
      </footer>
      </div>
    </>
  );
};

export default ReleaseNotes;
